import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/api/repository/axios';
import { mergeSlot, type PageBanner } from './pageBannerMerge';
import { resolvePageBanners, type PageBannerTarget } from './pageBannerRoutes';

/**
 * 지금 경로의 상단·하단 배너 — 페이지 위치 + "모든 페이지"를 **요청 한 번**으로 받는다(`GET /banners?locations=…`).
 * 같은 위치 묶음은 60초 동안 다시 묻지 않고(서버 캐시 TTL 과 같음), 상단·하단 자리와 목록 화면이 같은 요청을 함께 쓴다.
 * 실패하면 배너 없이 그린다(자리 0).
 */
const TTL_MS = 60_000;
type Entry = { at: number; data?: Record<string, PageBanner[]>; pending?: Promise<Record<string, PageBanner[]>> };
const store = new Map<string, Entry>();

function load(key: string, locations: string[]): Promise<Record<string, PageBanner[]>> {
    const hit = store.get(key);
    if (hit?.data && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.data);
    if (hit?.pending) return hit.pending;
    const pending = api.banner.getPageBanners(locations)
        .then(response => (response?.code === 0 && response.result ? response.result : {}) as Record<string, PageBanner[]>)
        .catch(() => ({} as Record<string, PageBanner[]>))
        .then(data => { store.set(key, { at: Date.now(), data }); return data; });
    store.set(key, { at: hit?.at ?? 0, data: hit?.data, pending });
    return pending;
}

function cached(key: string): Record<string, PageBanner[]> | undefined {
    const hit = store.get(key);
    return hit?.data && Date.now() - hit.at < TTL_MS ? hit.data : undefined;
}

export interface PageBannersState {
    target: PageBannerTarget;
    top: PageBanner[];
    bottom: PageBanner[];
}

export function usePageBanners(): PageBannersState {
    const { pathname } = useLocation();
    const target = resolvePageBanners(pathname);
    const key = target.locations.join(',');
    const [data, setData] = useState<{ key: string; value: Record<string, PageBanner[]> } | null>(() => {
        const value = key ? cached(key) : undefined;
        return value ? { key, value } : null;
    });

    useEffect(() => {
        if (!key) return;
        let alive = true;
        const value = cached(key);
        if (value) { setData({ key, value }); return; }
        load(key, target.locations).then(result => { if (alive) setData({ key, value: result }); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    const value = data?.key === key ? data.value : undefined;
    const [pageTop, pageBottom] = target.page ? target.locations.slice(0, 2) : [undefined, undefined];
    const [allTop, allBottom] = target.locations.slice(-2);
    return {
        target,
        top: target.excluded ? [] : mergeSlot(value, [pageTop, allTop].filter(Boolean) as string[]),
        bottom: target.excluded ? [] : mergeSlot(value, [pageBottom, allBottom].filter(Boolean) as string[]),
    };
}
